/**
 * Design System · Palette → Theme derivation layer.
 *
 * Product Owner directive (DSG-006 · 2026-07-03):
 *   > Brand Colour Palette is the source of truth. The application
 *   > theme is DERIVED — administrators never manage app-level tokens.
 *
 * This module is the ONLY place the palette → CSS variable mapping is
 * defined. Anything else in the app that needs a colour reads a semantic
 * CSS variable, never a hex code from the palette directly. Swapping
 * design systems later means editing this file only.
 */

// Palette tokens we accept from the backend. Keep this in sync with
// `THEME_COLOR_KEYS` in backend/routes/branding.py.
const PALETTE_KEYS = [
  "primary", "secondary", "accent",
  "success", "warning", "error", "information",
];

// Coolman default palette — mirrors the backend BRAND_PRESETS entry so
// we can render a preview even before the /api/branding fetch resolves.
export const COOLMAN_DEFAULT_PALETTE = {
  primary:     "#D71920",
  secondary:   "#111111",
  accent:      "#6B7280",
  success:     "#16A34A",
  warning:     "#F59E0B",
  error:       "#DC2626",
  information: "#2563EB",
};

export const PALETTE_LABELS = {
  primary:     "Primary",
  secondary:   "Secondary",
  accent:      "Accent",
  success:     "Success",
  warning:     "Warning",
  error:       "Error",
  information: "Information",
};

export const PALETTE_HINTS = {
  primary:     "Brand red — call-to-action buttons, active nav.",
  secondary:   "Near-black — primary text on light surfaces.",
  accent:      "Neutral detail — chips, subtle icons, dividers.",
  success:     "Positive states — completed tasks, delivered orders.",
  warning:     "Attention — overdue, requires action.",
  error:       "Destructive / failure states.",
  information: "Neutral information — help text, tooltips.",
};

/**
 * Apply the palette to :root as CSS variables. Every consumer reads
 * `var(--color-primary)` etc., NEVER the hex string directly.
 *
 * Also derives a hover shade (10% darker) for each palette entry so the
 * shell doesn't have to compute it. Kept intentionally simple — a full
 * design token pipeline (contrast pairs, tint scales) is V1.2 scope.
 */
export function publishPaletteAsCssVariables(palette) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const merged = { ...COOLMAN_DEFAULT_PALETTE, ...(palette || {}) };
  for (const key of PALETTE_KEYS) {
    const hex = merged[key];
    if (!hex) continue;
    root.style.setProperty(`--color-${key}`, hex);
    root.style.setProperty(`--color-${key}-hover`, darken(hex, 10));
    // Foreground pair — pick white or black based on YIQ luminance so
    // status badges remain legible without a designer touching them.
    root.style.setProperty(`--color-${key}-foreground`, readableForeground(hex));
  }
}

// ---------------------------------------------------------------------------
// Small colour helpers. Deliberately dependency-free — a full colour
// library is overkill for a 7-token palette.
// ---------------------------------------------------------------------------

function clamp(n) { return Math.max(0, Math.min(255, Math.round(n))); }

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  const v = h.length === 3
    ? h.split("").map((c) => c + c).join("")
    : h;
  return {
    r: parseInt(v.slice(0, 2), 16),
    g: parseInt(v.slice(2, 4), 16),
    b: parseInt(v.slice(4, 6), 16),
  };
}

function rgbToHex(r, g, b) {
  return "#" + [r, g, b].map((n) => clamp(n).toString(16).padStart(2, "0")).join("").toUpperCase();
}

/** Return a darker variant of the given hex (percent 0-100). */
export function darken(hex, percent) {
  const { r, g, b } = hexToRgb(hex);
  const f = 1 - percent / 100;
  return rgbToHex(r * f, g * f, b * f);
}

/** Choose white or black text for legibility on the given background. */
export function readableForeground(hex) {
  const { r, g, b } = hexToRgb(hex);
  // YIQ formula — matches WCAG's practical guidance for large text.
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 150 ? "#111111" : "#FFFFFF";
}

/**
 * WCAG 2.1 contrast ratio between two hex colours.
 * Returned to admins so they can see if their palette is accessible.
 */
export function contrastRatio(a, b) {
  const lum = (hex) => {
    const { r, g, b } = hexToRgb(hex);
    const chan = (c) => {
      const cs = c / 255;
      return cs <= 0.03928 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * chan(r) + 0.7152 * chan(g) + 0.0722 * chan(b);
  };
  const l1 = lum(a);
  const l2 = lum(b);
  const [hi, lo] = l1 > l2 ? [l1, l2] : [l2, l1];
  return (hi + 0.05) / (lo + 0.05);
}
